import { useId, type SVGProps } from "react";

// PwC "Graphic elements" icons — Figma ver.6 "브랜드 아이콘 (PwC Graphic elements)" 546:4597 의 공식 SVG 경로예요
// (REST /v1/images svg, 2026-10-13 수집). 32 x 32 격자에 채운 글리프이고 currentColor를 따라가요.
// Brand guide: no emoji or text symbols (ⓘ ⚠ ✦ 🔍 ★ ☆ ✓ ✕ 🔒) — use these instead.
// Figma ids: Person 546:4598 · AI agent 546:4600 · Search 546:4602 · Information 546:4610 · Alert 546:4612 ·
// Star 546:4614 · Check 546:4616 · Close 546:4618 · Lock 546:4621 · Picto 546:4605 / 4607 / 4609.
// Document · Download · Compare 는 에이전트 상세 CTA 의 16 x 16 선 아이콘(546:6499 · 6504 · 6514)이에요.
type IconProps = Omit<SVGProps<SVGSVGElement>, "width" | "height"> & { size?: number };

// 브랜드 글리프 공통 틀. 호출하는 쪽의 fill / stroke 속성이 글리프 색을 덮지 않도록 path 에 직접 채움을 둬요.
function Glyph({ size, children, ...props }: IconProps & { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden {...props}>
      {children}
    </svg>
  );
}

function NvPersonIcon({ size = 16, ...props }: IconProps) {
  return (
    <Glyph size={size} {...props}>
      <path
        fill="currentColor"
        d="M26 28L24.5996 30H6V23C6 20.7944 7.7939 19 10 19H22C24.2061 19 26 20.7944 26 23V26H24V23C24 21.897 23.1025 21 22 21H10C8.8975 21 8 21.897 8 23V28H26ZM8.5 9.5C8.5 5.3643 11.8643 2 16 2C20.1357 2 23.5 5.3643 23.5 9.5C23.5 13.6357 20.1357 17 16 17C11.8643 17 8.5 13.6357 8.5 9.5ZM10.5 9.5C10.5 12.5327 12.9678 15 16 15C19.0322 15 21.5 12.5327 21.5 9.5C21.5 6.4673 19.0322 4 16 4C12.9678 4 10.5 6.4673 10.5 9.5Z"
      />
    </Glyph>
  );
}

function NvStarIcon({ size = 20, ...props }: IconProps) {
  return (
    <Glyph size={size} {...props}>
      <path
        fill="currentColor"
        d="M7.46924 14H14.1404L16 8.37347L17.8596 14H24.5308L18.9196 18.7373L21.4749 24.6115L19.4811 23.2936L17.7628 24.5553L26 30L21.3475 19.3049L30 12H19.3049L16 2L12.6951 12H2L10.6525 19.3049L6 30L17.6955 22.1133L16 20.9927L10.5251 24.6115L13.0804 18.7373L7.46924 14Z"
      />
    </Glyph>
  );
}

function NvCheckIcon({ size = 13, ...props }: IconProps) {
  return (
    <Glyph size={size} {...props}>
      <path fill="currentColor" d="M27.997 5.67871L12.852 23.083L5.2832 16.0898L3.92578 17.5586L13.0083 25.9502L27.9985 8.72345V5.67999L27.997 5.67871Z" />
    </Glyph>
  );
}

// Brand "Close" is an X inside a square.
function NvCloseIcon({ size = 16, ...props }: IconProps) {
  return (
    <Glyph size={size} {...props}>
      <path
        fill="currentColor"
        d="M9.33191 24.0799L16.0019 17.4099L22.6719 24.0799L24.0819 22.6699L17.4119 15.9999L22.6119 10.7999V7.97992L16.0019 14.5899L9.33191 7.91992L7.92188 9.3299L14.5919 15.9999L7.92188 22.6699L9.33191 24.0799Z"
      />
      <path
        fill="currentColor"
        d="M28 4H4V28H26.5938L27.9732 26.03V26H6V6H25.9896L25.9753 24H27.9758L27.9934 4.05273L27.9902 4.05389L28 4Z"
      />
    </Glyph>
  );
}

function NvInfoIcon({ size = 16, ...props }: IconProps) {
  return (
    <Glyph size={size} {...props}>
      <path
        fill="currentColor"
        d="M2 16.0507C2 8.33093 8.28027 2.05066 16 2.05066C23.7197 2.05066 30 8.33093 30 16.0507C30 23.4329 24.2529 29.4837 17 30V28C23.1499 27.489 28 22.3298 28 16.0507C28 9.43396 22.6172 4.05066 16 4.05066C9.38281 4.05066 4 9.43396 4 16.0507C4 22.2844 8.77881 27.4191 14.8652 27.9932L13.5812 29.8268C7.01245 28.6759 2 22.9442 2 16.0507ZM17 14.6802H15V22.7105H17V14.6802ZM17 10.3829H15V12.3829H17V10.3829Z"
      />
    </Glyph>
  );
}

function NvAlertIcon({ size = 16, ...props }: IconProps) {
  return (
    <Glyph size={size} {...props}>
      <path
        fill="currentColor"
        d="M27.2075 28H2.35156L16.0015 1.83789L27.5644 24H25.3081L16.0015 6.16209L5.65136 26H28.6079L27.2075 28ZM15.0015 20H17.0015V11H15.0015V20ZM15.0015 24H17.0015V22H15.0015V24Z"
      />
    </Glyph>
  );
}

function NvLockIcon({ size = 16, ...props }: IconProps) {
  return (
    <Glyph size={size} {...props}>
      <path
        fill="currentColor"
        d="M16.8658 18H19.1756L15.1343 25H12.8245L16.8658 18ZM25.9999 28L24.5995 30H6V13H9V9C9 5.1401 12.1401 2 16 2C19.8599 2 23 5.1401 23 9V13H26V26H24V15H8V28H25.9999ZM11 13H21V9C21 6.2432 18.7568 4 16 4C13.2432 4 11 6.2432 11 9V13Z"
      />
    </Glyph>
  );
}

function NvSearchIcon({ size = 16, ...props }: IconProps) {
  return (
    <Glyph size={size} {...props}>
      <path
        fill="currentColor"
        d="M11.0736 21H13.3834L8.4824 29.4888L6.75 28.4888L11.0736 21ZM27.0019 11C27.0019 15.9624 22.9643 20 18.0019 20C13.0395 20 9.0019 15.9624 9.0019 11C9.0019 6.0376 13.0395 2 18.0019 2C22.9643 2 27.0019 6.0376 27.0019 11ZM25.0019 11C25.0019 7.1401 21.8613 4 18.0019 4C14.1425 4 11.0019 7.1401 11.0019 11C11.0019 14.8599 14.1425 18 18.0019 18C21.8613 18 25.0019 14.8599 25.0019 11Z"
      />
    </Glyph>
  );
}

// "AI agent" = pen with sparkles (used for the AI interpretation chip and AI Assistant).
function NvAiAgentIcon({ size = 16, ...props }: IconProps) {
  return (
    <Glyph size={size} {...props}>
      <path
        fill="currentColor"
        d="M11.2336 10.3408L13.0066 10.9969L11.2336 11.6529C10.7374 11.8365 10.3462 12.2277 10.1626 12.7239L9.50656 14.4969L8.85051 12.7239C8.6669 12.2277 8.27569 11.8365 7.7795 11.6529L6.00656 10.9969L7.7795 10.3408C8.27569 10.1572 8.6669 9.76598 8.85051 9.26979L9.50656 7.49685L10.1626 9.2698C10.3462 9.76598 10.7374 10.1572 11.2336 10.3408ZM5.95246 15.3355L6.98828 14.9522L5.95246 14.5689C5.68334 14.4693 5.47115 14.2571 5.37156 13.988L4.98828 12.9522L4.60499 13.988C4.50541 14.2571 4.29322 14.4693 4.02409 14.5689L2.98828 14.9522L4.02409 15.3355C4.29322 15.4351 4.50541 15.6472 4.60499 15.9164L4.98828 16.9522L5.37156 15.9164C5.47115 15.6472 5.68334 15.4351 5.95246 15.3355ZM6.97075 6.37144L8.00656 5.98816L6.97074 5.60487C6.70162 5.50528 6.48943 5.2931 6.38984 5.02397L6.00656 3.98816L5.62327 5.02397C5.52369 5.2931 5.3115 5.50529 5.04237 5.60487L4.00656 5.98816L5.04237 6.37144C5.3115 6.47103 5.52369 6.68322 5.62327 6.95234L6.00656 7.98816L6.38984 6.95234C6.48943 6.68322 6.70162 6.47103 6.97075 6.37144ZM11.8896 24.4883L11.9643 26.3262L13.8979 25.8379L19.0029 17.8927L16.939 16.5667L11.8896 24.4883ZM29.934 10.166L23.8696 19.3467L22.2006 18.2451L27.1811 10.7051L22.6537 7.60155L18.0145 14.8796L20.0844 16.2095L22.2848 12.7847H24.6627L15.1537 27.584L10.0659 28.8672L9.86615 23.9414L22.0785 4.78319L29.934 10.166Z"
      />
    </Glyph>
  );
}

// 에이전트 상세 CTA · 서브 CTA 의 16 x 16 선 아이콘 (Figma 546:6499 · 6504 · 6514). 선 굵기는 시안 그대로 16 격자 기준이에요.
const line16 = { fill: "none", stroke: "currentColor", strokeLinecap: "round", strokeLinejoin: "round" } as const;

// 문서 + 내려받기 화살표 ("Agent 정의서 보기" 주 CTA, 546:6499)
function NvDocumentIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <path d="M4 2H9L12 5V14H4V2Z" stroke="currentColor" strokeLinecap="butt" strokeLinejoin="miter" />
      <path d="M9 2V5H12M8 7V12M10 10L8 12L6 10" stroke="currentColor" strokeLinecap="butt" strokeLinejoin="miter" />
    </svg>
  );
}

// 아래 화살표 + 받침 ("Agent 정의서 다운로드" 서브 CTA, 546:6504)
function NvDownloadIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden {...props}>
      <path d="M8 2.5V10.5M11.2 7.3L8 10.5L4.8 7.3M3 13.2H13" stroke="currentColor" strokeLinecap="butt" strokeLinejoin="miter" />
    </svg>
  );
}

// 상자에 담기 ("비교함 담기" 서브 CTA, 546:6514)
function NvCompareIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden {...props}>
      <path
        {...line16}
        strokeWidth={1.4}
        d="M7.30078 0.799805V6.7998M9.50078 4.5998L7.30078 6.7998L5.10078 4.5998M11.3008 4.7998H12.3008L13.8008 9.2998V12.7998C13.8008 13.3498 13.3508 13.7998 12.8008 13.7998H1.80078C1.25078 13.7998 0.800781 13.3498 0.800781 12.7998V9.2998L2.30078 4.7998H3.30078M0.800781 9.2998H4.30078L5.50078 11.2998H9.10078L10.3008 9.2998H13.8008"
      />
    </svg>
  );
}

// 아래는 ver.6 1차 시안에 따로 정의된 그림이 없어 같은 선 스타일로 그린 보조 아이콘이에요.
const outline = { fill: "none", stroke: "currentColor", strokeWidth: 1.5, strokeLinecap: "round", strokeLinejoin: "round" } as const;

function NvPlayIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden {...props}>
      <path d="M8 5.5v13l11-6.5-11-6.5z" fill="currentColor" />
    </svg>
  );
}

function NvExpandIcon({ size = 16, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden {...props}>
      <path {...outline} d="M4.5 9.5v-5h5M19.5 9.5v-5h-5M4.5 14.5v5h5M19.5 14.5v5h-5M4.5 4.5l5 5M19.5 4.5l-5 5M4.5 19.5l5-5M19.5 19.5l-5-5" />
    </svg>
  );
}

function NvChevronIcon({ size = 16, direction = "down", ...props }: IconProps & { direction?: "down" | "up" | "right" }) {
  const rotate = direction === "up" ? 180 : direction === "right" ? -90 : 0;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ transform: `rotate(${rotate}deg)` }} {...props}>
      <path {...outline} d="M6 9.5l6 6 6-6" />
    </svg>
  );
}

function NvExternalIcon({ size = 14, ...props }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden {...props}>
      <path {...outline} d="M14 4.5h5.5V10M19.5 4.5l-8 8M18 13.5v5a1 1 0 01-1 1H6a1 1 0 01-1-1V8a1 1 0 011-1h5" />
    </svg>
  );
}

// ── Picto (오렌지 그라데이션 선 그림, Figma "Picto / Messages · Data analysis · Robot" 546:4604~4608) ──
// 시안의 Picto 인스턴스는 44 x 44 상자 안에 벡터(38 x 38 · 38 x 36 · 22 x 37)를 가운데쯤 두는 구조라
// 같은 오프셋으로 공식 경로를 넣었어요. 그라데이션 색 단계도 시안 그대로예요.
function PictoGradient({ id, x1, y1, x2, y2 }: { id: string; x1: number; y1: number; x2: number; y2: number }) {
  return (
    <defs>
      <linearGradient id={id} x1={x1} y1={y1} x2={x2} y2={y2} gradientUnits="userSpaceOnUse">
        <stop stopColor="#FF9F00" />
        <stop offset="0.05089" stopColor="#FE9900" />
        <stop offset="0.466323" stopColor="#FD7204" />
        <stop offset="0.797112" stopColor="#FD5907" />
        <stop offset="1" stopColor="#FD5108" />
      </linearGradient>
    </defs>
  );
}

function NvPictoMessages({ size = 44, ...props }: IconProps) {
  const gid = `nv-picto-m-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-hidden {...props}>
      <g transform="translate(3 3)">
        <PictoGradient id={gid} x1={-2.55486} y1={34.7898} x2={28.2979} y2={3.93696} />
        <path
          fill={`url(#${gid})`}
          d="M20.8798 5.20517H4.17541V3.81132H20.8798V5.20517ZM15.2826 7.2939H4.17541V8.68774H15.2826V7.2939ZM36.1713 17.8212H19.4669V19.2151H36.1713V17.8212ZM36.1713 21.3038H25.0641V22.6976H36.1713V21.3038ZM38.9808 22.961V14.6198H19.4887C17.9397 14.6198 16.6792 15.8795 16.6792 17.4285V25.7699H36.1726C37.7217 25.7699 38.9808 24.5101 38.9808 22.961ZM18.0731 17.4285C18.0731 16.6486 18.7087 16.0136 19.4887 16.0136H37.5869V22.961C37.5869 23.741 36.9526 24.376 36.1726 24.376H18.0731V17.4285ZM19.5186 31.0451H2.81424V32.439H19.5186V31.0451ZM13.9214 34.5277H2.81424V35.9215H13.9214V34.5277ZM20.9077 35.5152V30.6507C20.9077 29.8701 20.2805 29.2429 19.4999 29.2429H1.39385V36.1842C1.39385 36.9648 2.03496 37.606 2.81552 37.606H22.3016V37.6199L21.5071 38.9998H2.81552C1.26836 38.9998 0 37.7314 0 36.1842V27.8491H19.4999C21.047 27.8491 22.3016 29.1035 22.3016 30.6507V35.5152H20.9077ZM23.6675 9.05999V2.81551C23.6675 1.25441 22.413 0 20.8519 0H1.36595V9.74298C1.36595 11.2901 2.62044 12.5446 4.18154 12.5446H4.18631V17.9329L9.57455 12.5446H22.859L23.6675 11.1508H8.99025L8.68367 11.4574V11.4645L5.58016 14.568V12.5446H5.58933V11.1508H4.18154C3.40099 11.1508 2.75979 10.5235 2.75979 9.74298V1.39385H20.8519C21.6324 1.39385 22.2737 2.03496 22.2737 2.81551V9.05999H23.6675Z"
        />
      </g>
    </svg>
  );
}

function NvPictoDataAnalysis({ size = 44, ...props }: IconProps) {
  const gid = `nv-picto-d-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-hidden {...props}>
      <g transform="translate(4 4)">
        <PictoGradient id={gid} x1={0.518181} y1={36.4455} x2={30.2349} y2={6.72872} />
        <path
          fill={`url(#${gid})`}
          d="M38 34.5455V35.9273H0V0H1.38182V34.5455H38ZM14.5778 23.806C16.3691 24.9131 18.4712 25.5636 20.7273 25.5636C27.2039 25.5636 32.4727 20.2941 32.4727 13.8182C32.4727 7.8104 27.9357 2.84851 22.1091 2.16135V3.55772C27.1713 4.23678 31.0909 8.57292 31.0909 13.8182C31.0909 19.533 26.4421 24.1818 20.7273 24.1818C15.0124 24.1818 10.3636 19.533 10.3636 13.8182C10.3636 8.62807 14.2018 4.32988 19.1867 3.58198L20.2257 2.09809C13.9826 2.36358 8.98182 7.5115 8.98182 13.8182C8.98182 17.5429 10.7304 20.8618 13.4437 23.0153L7.30534 31.7818H8.99312L14.5778 23.806ZM12.4364 15.2V17.2673C12.7834 18.0981 13.2507 18.8662 13.8182 19.5492V15.2H12.4364ZM15.2 9.67273V20.8831C15.6314 21.2214 16.0924 21.5223 16.5818 21.7783V9.67273H15.2ZM17.9636 5.52727V22.3604C18.4096 22.5051 18.8722 22.6089 19.3455 22.6825V5.52727H17.9636ZM20.7273 5.52727V22.8C21.1983 22.8 21.6575 22.7527 22.1091 22.6825V5.52727H20.7273ZM23.4909 13.7804V22.3604C23.9707 22.2048 24.4319 22.0087 24.8727 21.7783V13.7804H23.4909ZM26.2545 15.2V20.8831C26.76 20.4868 27.2261 20.0429 27.6364 19.5492V15.2H26.2545Z"
        />
      </g>
    </svg>
  );
}

function NvPictoRobot({ size = 44, ...props }: IconProps) {
  const gid = `nv-picto-r-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <svg width={size} height={size} viewBox="0 0 44 44" fill="none" aria-hidden {...props}>
      <g transform="translate(11 3)">
        <PictoGradient id={gid} x1={-0.795405} y1={32.7229} x2={22.2709} y2={9.65662} />
        <path
          fill={`url(#${gid})`}
          d="M8.14142 9.56846C6.79997 9.56846 5.70817 10.6603 5.70817 12.0017C5.70817 13.3437 6.79997 14.4356 8.14142 14.4356C9.48344 14.4356 10.5753 13.3437 10.5753 12.0017C10.5753 10.6603 9.48344 9.56846 8.14142 9.56846ZM8.14142 13.0465C7.56568 13.0465 7.09722 12.5781 7.09722 12.0017C7.09722 11.426 7.56568 10.9575 8.14142 10.9575C8.71779 10.9575 9.18624 11.426 9.18624 12.0017C9.18624 12.5781 8.71779 13.0465 8.14142 13.0465ZM17.6261 28.8724V23.6291H4.37214V28.8724H17.6261ZM5.7612 25.0181H16.237V27.4834H5.7612V25.0181ZM13.8568 9.56846C12.5148 9.56846 11.4229 10.6603 11.4229 12.0017C11.4229 13.3437 12.5148 14.4356 13.8568 14.4356C15.1983 14.4356 16.2901 13.3437 16.2901 12.0017C16.2901 10.6603 15.1983 9.56846 13.8568 9.56846ZM13.8568 13.0465C13.2804 13.0465 12.812 12.5781 12.812 12.0017C12.812 11.426 13.2804 10.9575 13.8568 10.9575C14.4326 10.9575 14.901 11.426 14.901 12.0017C14.901 12.5781 14.4326 13.0465 13.8568 13.0465ZM21.9982 30.5731V21.6239C21.9982 20.1679 20.8141 18.9832 19.3582 18.9832H14.3367V17.3198H19.5475V9.32491C19.5475 7.86893 18.3628 6.68422 16.9068 6.68422H11.6936V5.59545H12.6143L14.2294 2.79801L12.6143 0H9.38398L7.76885 2.79801L9.38398 5.59545H10.3046V6.68422H5.09142C3.63544 6.68422 2.45073 7.86893 2.45073 9.32491V17.3198H7.66155V18.9832H2.64007C1.18409 18.9832 0 20.1679 0 21.6239V33.5183H7.66155V37.4575H9.05061V33.5183H12.9476V37.4575H14.3367V33.5183H20.9528L21.7744 32.1292H1.38906V21.6239C1.38906 20.9336 1.95036 20.3723 2.64007 20.3723H19.3582C20.0479 20.3723 20.6092 20.9336 20.6092 21.6239V30.5731H21.9982ZM10.1858 4.20639L9.3725 2.79801L10.1858 1.38906H11.8124L12.6257 2.79801L11.8124 4.20639H10.1858ZM3.83979 15.9308V9.32491C3.83979 8.63458 4.40109 8.07328 5.09142 8.07328H16.9068C17.5971 8.07328 18.1584 8.63458 18.1584 9.32491V15.9308H3.83979ZM12.9476 18.9832H9.05061V17.3198H12.9476V18.9832Z"
        />
      </g>
    </svg>
  );
}

export {
  NvAiAgentIcon,
  NvChevronIcon,
  NvCompareIcon,
  NvDocumentIcon,
  NvDownloadIcon,
  NvExpandIcon,
  NvExternalIcon,
  NvPlayIcon,
  NvAlertIcon,
  NvCheckIcon,
  NvCloseIcon,
  NvInfoIcon,
  NvLockIcon,
  NvPersonIcon,
  NvPictoDataAnalysis,
  NvPictoMessages,
  NvPictoRobot,
  NvSearchIcon,
  NvStarIcon,
};
